import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Buildings } from "@phosphor-icons/react";

export const regLabel = (r) => r?.name || r?.nerc_id || r?.id;

// Reusable checkbox list of registered entities.
export function EntityCheckboxList({ registrations = [], value = [], onChange, testidPrefix = "entity" }) {
  const set = new Set(value);
  const toggle = (id) => {
    const next = new Set(set);
    next.has(id) ? next.delete(id) : next.add(id);
    onChange(Array.from(next));
  };
  if (!registrations.length) {
    return <p className="text-xs text-muted-foreground">No entities available.</p>;
  }
  return (
    <div className="max-h-52 overflow-y-auto border border-border divide-y divide-border" data-testid={`${testidPrefix}-list`}>
      {registrations.map((r) => (
        <label key={r.id} className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-secondary/40"
          data-testid={`${testidPrefix}-option-${r.id}`}>
          <input type="checkbox" checked={set.has(r.id)} onChange={() => toggle(r.id)}
            data-testid={`${testidPrefix}-checkbox-${r.id}`} />
          <span className="font-medium">{regLabel(r)}</span>
          {r.nerc_id && r.name && <span className="text-xs text-muted-foreground font-mono">{r.nerc_id}</span>}
        </label>
      ))}
    </div>
  );
}

// Edit dialog: assign an existing user to multiple registered entities.
export default function AssignEntitiesDialog({ open, onOpenChange, user, registrations = [], onSaved }) {
  const assignableIds = useMemo(() => new Set(registrations.map((r) => r.id)), [registrations]);
  const [sel, setSel] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open && user) {
      const cur = (user.allowed_registration_ids || []).filter((id) => assignableIds.has(id));
      setSel(cur);
    }
  }, [open, user, assignableIds]);

  const save = async () => {
    setBusy(true);
    try {
      // Send only in-scope selections; the backend preserves memberships in entities
      // this admin doesn't manage.
      await api.put(`/users/${user.id}/registrations`, { registration_ids: Array.from(new Set(sel)) });
      toast.success("Entities updated");
      onOpenChange(false);
      onSaved && onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Update failed");
    } finally { setBusy(false); }
  };

  if (!user) return null;
  const hiddenCount = (user.allowed_registration_ids || []).filter((id) => !assignableIds.has(id)).length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none" data-testid="assign-entities-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Buildings size={18} /> Entities — {user.name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-xs text-muted-foreground">
            Select every registered entity this user should have access to. Their role ({user.role}) applies in each entity.
          </p>
          <EntityCheckboxList registrations={registrations} value={sel} onChange={setSel} testidPrefix="assign-entity" />
          {hiddenCount > 0 && (
            <p className="text-[11px] text-muted-foreground" data-testid="assign-entities-hidden-note">
              This user also belongs to {hiddenCount} entit{hiddenCount === 1 ? "y" : "ies"} you don't manage — those stay unchanged.
            </p>
          )}
          {sel.length === 0 && hiddenCount === 0 && (
            <p className="text-[11px] text-amber-700 dark:text-amber-500" data-testid="assign-entities-deactivate-note">
              Removing this user from their last entity will <strong>deactivate their account</strong> — their record, Claro ID and history are retained, and access is restored automatically when you re-add an entity.
            </p>
          )}
          <Button data-testid="save-entities-btn" onClick={save} disabled={busy}
            className="rounded-none w-full bg-primary hover:bg-primary/90">{busy ? "Saving…" : (sel.length === 0 && hiddenCount === 0 ? "Remove & deactivate" : "Save entities")}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
